/**
 * Charge amount in the LISTING's own currency.
 *
 * The diver browses (and sees totals) in the currency they selected, which the
 * UI builds from the listing prices converted and rounded to whole units. That
 * rounding plus the conversion back to the charge currency used to lose money
 * on every cross-currency booking (listing 70 EUR -> shown 80 USD -> charged
 * 139 EUR instead of 140 EUR).
 *
 * The functions below rebuild the same total with the listing's own prices and
 * NO conversion, so the charge is the exact sum of the listing prices. The
 * formula mirrors app/components/listings/ListingReservation.tsx (totals) and
 * app/components/booking/BookingReservation.tsx (coupon), so the charge stays
 * consistent with what the diver was shown (up to the display conversion).
 */

export interface ListingChargePrices {
  price: number;
  priceExtraDiver?: number | null;
  priceExtraSnorkeler?: number | null;
  priceNitrox?: number | null;
  price15L?: number | null;
  priceFullSet?: number | null;
  priceComputer?: number | null;
  priceTorch?: number | null;
  priceAdapter?: number | null;
  priceBCD?: number | null;
  priceReg?: number | null;
  priceWetsuit?: number | null;
  priceMask?: number | null;
  priceFins?: number | null;
  priceWeights?: number | null;
  pricePrivateGuide?: number | null;
  priceFees?: number | null;
  discountTotal?: number | null;
  discountWeekly?: number | null;
  /** Comma separated list (Prisma String), or already split. */
  included?: string | string[] | null;
  notIncluded?: string | string[] | null;
  currency?: string | null;
}

export interface ListingChargeQuantities {
  participants?: number | null;
  extraDiver?: number | null;
  extraSnorkeler?: number | null;
  nitrox?: number | null;
  l15?: number | null;
  fullSet?: number | null;
  computer?: number | null;
  torch?: number | null;
  adapter?: number | null;
  bcd?: number | null;
  reg?: number | null;
  wetsuit?: number | null;
  mask?: number | null;
  fins?: number | null;
  weights?: number | null;
  privateGuide?: number | null;
  /** Number of booked days (dates.length); participants and extras are per day. */
  days?: number | null;
}

/**
 * The web and the mobile app build their totals slightly differently, so the
 * charge follows the rules of the client the booking came from. Defaults are
 * the web rules (app/components/listings/ListingReservation.tsx).
 */
export interface ListingChargeOptions {
  /** Equipment is charged per booked day (mobile) instead of once (web). */
  equipmentPerDay?: boolean;
  /** Use the weekly discount on 7+ day bookings (mobile only). */
  useWeeklyDiscount?: boolean;
  /** Fees are discounted together with the price (mobile) instead of added after (web). */
  feesBeforeDiscount?: boolean;
  /** Fees are charged once per booking (mobile) instead of per participant (web). */
  feesPerBooking?: boolean;
  /** Fees are charged whenever the listing has them (mobile), not only when "not included" (web). */
  alwaysChargeFees?: boolean;
}

export interface ListingChargeCoupon {
  discountType?: string | null;
  discountValue?: number | null;
  /** Currency of a FIXED coupon (percentage coupons ignore it). */
  currency?: string | null;
}

/**
 * Every money value is stored (and shown) with 2 decimals, so what lands in
 * the database is exactly what Stripe is charged: Stripe minor units are
 * `round(amount * 100)`, and `roundMoney` is the same rounding expressed in
 * major units. Never call it on a value already in minor units.
 */
export function roundMoney(value: number | null | undefined): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/** `roundMoney` for nullable columns: keeps null/undefined as-is. */
export function roundMoneyOrNull<T extends number | null | undefined>(
  value: T
): T extends number ? number : number | null {
  if (value === null || value === undefined) return null as never;
  return roundMoney(value) as never;
}

const num = (value: number | null | undefined): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const count = (value: number | null | undefined): number => {
  const n = Math.trunc(Number(value));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

/** True when the listing charges its fees on top of the price (vs included). */
export function listingFeesAreExtra(listing: ListingChargePrices): boolean {
  const notIncluded = listing.notIncluded;
  if (Array.isArray(notIncluded)) return notIncluded.includes("Fees");
  // Same check the booking UI does on the raw string.
  return typeof notIncluded === "string" ? notIncluded.includes("Fees") : false;
}

/**
 * Subtotal + listing discount + fees, all in the listing's own currency.
 * Coupons are applied separately (see applyCouponInListingCurrency).
 */
export function computeListingCurrencyTotal(
  listing: ListingChargePrices,
  quantities: ListingChargeQuantities,
  options: ListingChargeOptions = {}
): number {
  const days = Math.max(1, count(quantities.days) || 1);
  const participants = count(quantities.participants);

  // Per-person items are charged per booked day, equipment is charged once.
  const perDay =
    num(listing.price) * participants +
    num(listing.priceExtraDiver) * count(quantities.extraDiver) +
    num(listing.priceExtraSnorkeler) * count(quantities.extraSnorkeler);

  const equipment =
    num(listing.priceNitrox) * count(quantities.nitrox) +
    num(listing.price15L) * count(quantities.l15) +
    num(listing.priceFullSet) * count(quantities.fullSet) +
    num(listing.priceComputer) * count(quantities.computer) +
    num(listing.priceTorch) * count(quantities.torch) +
    num(listing.priceAdapter) * count(quantities.adapter) +
    num(listing.priceBCD) * count(quantities.bcd) +
    num(listing.priceReg) * count(quantities.reg) +
    num(listing.priceWetsuit) * count(quantities.wetsuit) +
    num(listing.priceMask) * count(quantities.mask) +
    num(listing.priceFins) * count(quantities.fins) +
    num(listing.priceWeights) * count(quantities.weights) +
    num(listing.pricePrivateGuide) * count(quantities.privateGuide);

  const subtotal =
    perDay * days + (options.equipmentPerDay ? equipment * days : equipment);

  // Fees: the web adds them per participant after the discount, the app adds
  // them once before it.
  const chargesFees = options.alwaysChargeFees
    ? num(listing.priceFees) > 0
    : listingFeesAreExtra(listing);
  const fees = chargesFees
    ? num(listing.priceFees) * (options.feesPerBooking ? 1 : participants)
    : 0;

  // The web only ever applies discountTotal (its weekly branch is unreachable);
  // the app uses the weekly discount on 7+ day bookings.
  const discountWeekly = num(listing.discountWeekly);
  const discountTotal = num(listing.discountTotal);
  const discountPercent =
    options.useWeeklyDiscount && discountWeekly > 0 && days >= 7
      ? discountWeekly
      : discountTotal;

  const discountBase = options.feesBeforeDiscount ? subtotal + fees : subtotal;
  const afterDiscount =
    discountPercent !== 0
      ? discountBase - (discountBase * discountPercent) / 100
      : discountBase;

  const total = options.feesBeforeDiscount ? afterDiscount : afterDiscount + fees;
  return total > 0 ? roundMoney(total) : 0;
}

/**
 * Applies a coupon to a listing-currency amount, the same way the booking UI
 * applies it to the diver-currency amount. FIXED coupons are stored in their
 * own currency, so only that discount is converted.
 */
export function applyCouponInListingCurrency(
  amount: number,
  coupon: ListingChargeCoupon | null | undefined,
  listingCurrency: string,
  convert: (value: number, from: string, to: string) => number
): number {
  if (!coupon) return amount;
  const value = num(coupon.discountValue);
  if (value <= 0) return amount;

  if (coupon.discountType === "PERCENTAGE") {
    return roundMoney(Math.max(0, amount - (amount * value) / 100));
  }

  if (coupon.discountType === "FIXED") {
    const couponCurrency = (coupon.currency || "EUR").toUpperCase();
    const target = (listingCurrency || "EUR").toUpperCase();
    const discount =
      couponCurrency === target ? value : convert(value, couponCurrency, target);
    if (!Number.isFinite(discount)) return amount;
    return roundMoney(Math.max(0, amount - discount));
  }

  return amount;
}

/**
 * The total BEFORE the coupon, rebuilt from the amount actually charged.
 *
 * The dive center is always paid its share of the undiscounted price: a coupon
 * is Be Underwater marketing and its cost comes out of the platform's side, so
 * every charge path needs this number.
 *
 * It used to be `charged / (1 - discountValue / 100)` everywhere, which silently
 * assumed a PERCENTAGE coupon. On a FIXED coupon that formula reads the amount
 * as a percentage: 50 EUR off a 200 EUR booking rebuilt 300 instead of 200 and
 * overpaid the center, and a 100 EUR coupon divided by zero.
 */
export function amountBeforeCoupon(
  chargedAmount: number,
  coupon: ListingChargeCoupon | null | undefined,
  chargeCurrency: string,
  convert: (value: number, from: string, to: string) => number
): number {
  const charged = num(chargedAmount);
  if (!coupon) return roundMoney(charged);
  const value = num(coupon.discountValue);
  if (value <= 0) return roundMoney(charged);

  if (coupon.discountType === "PERCENTAGE") {
    // A 100% (or larger) coupon leaves nothing to scale back up, so the charged
    // amount is the best we can say.
    if (value >= 100) return roundMoney(charged);
    return roundMoney(charged / (1 - value / 100));
  }

  if (coupon.discountType === "FIXED") {
    const couponCurrency = (coupon.currency || "EUR").toUpperCase();
    const target = (chargeCurrency || "EUR").toUpperCase();
    const discount =
      couponCurrency === target ? value : convert(value, couponCurrency, target);
    if (!Number.isFinite(discount) || discount <= 0) return roundMoney(charged);
    return roundMoney(charged + discount);
  }

  return roundMoney(charged);
}

/** Currencies Stripe expects WITHOUT decimals (amount is the whole unit). */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF", "CLP", "DJF", "GNF", "JPY", "KMF", "KRW", "MGA", "PYG", "RWF",
  "UGX", "VND", "VUV", "XAF", "XOF", "XPF",
]);

/** Currencies Stripe expects with three decimals, in multiples of 10. */
const THREE_DECIMAL_CURRENCIES = new Set(["BHD", "JOD", "KWD", "OMR", "TND"]);

/** Stripe minor units, rounded to the nearest unit (never rounded down). */
export function toStripeMinorUnits(amount: number, currency: string): number {
  const code = (currency || "EUR").toUpperCase();
  const value = Number(amount) || 0;
  if (ZERO_DECIMAL_CURRENCIES.has(code)) {
    return Math.round(value);
  }
  if (THREE_DECIMAL_CURRENCIES.has(code)) {
    return Math.round((value * 1000) / 10) * 10;
  }
  return Math.round(value * 100);
}

/**
 * The currency the Stripe account settles in, and what every charge falls back
 * to: EUR on the original Spanish account, USD on the LLC account. Set with
 * NEXT_PUBLIC_BUW_DEFAULT_CHARGE_CURRENCY (the checkout shows the charge, so
 * the browser needs it too) or BUW_DEFAULT_CHARGE_CURRENCY.
 */
export function getDefaultChargeCurrency(): string {
  const code = String(
    process.env.NEXT_PUBLIC_BUW_DEFAULT_CHARGE_CURRENCY ||
      process.env.BUW_DEFAULT_CHARGE_CURRENCY ||
      ""
  )
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : "EUR";
}

/**
 * Currencies we are allowed to charge the card in. Charging outside this list
 * would move the FX conversion (and Stripe's conversion fee) onto the Be
 * Underwater account and would change the payout currency, so it stays opt-in:
 * the default charge currency only, extended with BUW_CHARGE_CURRENCIES
 * (comma separated) when payouts for those currencies are ready.
 */
export function getAllowedChargeCurrencies(
  envValue?: string | null
): Set<string> {
  const allowed = new Set<string>([getDefaultChargeCurrency()]);
  (envValue || "")
    .split(",")
    .map((code) => code.trim().toUpperCase())
    .filter((code) => /^[A-Z]{3}$/.test(code))
    .forEach((code) => allowed.add(code));
  return allowed;
}

/** The currency the card is actually charged in for a given listing. */
export function resolveChargeCurrency(
  listingCurrency: string | null | undefined,
  envValue?: string | null
): string {
  const fallback = getDefaultChargeCurrency();
  const code = String(listingCurrency || fallback).toUpperCase();
  return getAllowedChargeCurrencies(envValue).has(code) ? code : fallback;
}

/**
 * The charge amount must stay close to what the diver was shown (converted).
 * A larger gap means the recomputation disagrees with the booking UI, and the
 * caller falls back to the legacy converted amount instead of charging it.
 */
export function chargeAmountLooksConsistent(
  listingCurrencyAmount: number,
  legacyEquivalentInListingCurrency: number
): boolean {
  if (!Number.isFinite(listingCurrencyAmount) || listingCurrencyAmount < 0) {
    return false;
  }
  if (
    !Number.isFinite(legacyEquivalentInListingCurrency) ||
    legacyEquivalentInListingCurrency <= 0
  ) {
    return false;
  }
  const tolerance = Math.max(
    legacyEquivalentInListingCurrency * 0.03,
    2 // absolute slack for per-unit rounding in the displayed prices
  );
  return (
    Math.abs(listingCurrencyAmount - legacyEquivalentInListingCurrency) <=
    tolerance
  );
}

export interface ResolvedBookingCharge {
  /** Amount to charge, in `currency`. Never rounded down. */
  amount: number;
  /** Currency the card is charged in (what Stripe receives). */
  currency: string;
  /** The listing's own currency. */
  listingCurrency: string;
  /** Exact total built from the listing prices, in the listing currency. */
  listingCurrencyTotal: number;
  /**
   * True when the recomputed total disagreed with what the diver was shown and
   * the legacy converted amount was kept instead (needs a look).
   */
  usedFallback: boolean;
}

/**
 * Single source of truth for the amount + currency of a listing booking charge.
 *
 * - The total is rebuilt from the listing's own prices (no diver-currency round
 *   trip, no whole-unit rounding).
 * - It is charged in the listing currency when that currency is allowed,
 *   otherwise converted ONCE to the default charge currency.
 * - If the rebuilt total does not match what the diver was shown, the legacy
 *   amount is used so nobody is charged a surprising number.
 */
export function resolveBookingCharge(params: {
  listing: ListingChargePrices;
  quantities: ListingChargeQuantities;
  /** Totals rules of the client the booking came from (web by default). */
  options?: ListingChargeOptions;
  coupon?: ListingChargeCoupon | null;
  convert: (value: number, from: string, to: string) => number;
  /** Amount the client asked to charge, in EUR (legacy behaviour). */
  legacyAmountEur: number;
  chargeCurrenciesEnv?: string | null;
}): ResolvedBookingCharge {
  const {
    listing,
    quantities,
    options,
    coupon,
    convert,
    legacyAmountEur,
    chargeCurrenciesEnv,
  } = params;

  const listingCurrency = String(listing.currency || "EUR").toUpperCase();
  const listingCurrencyTotal = applyCouponInListingCurrency(
    computeListingCurrencyTotal(listing, quantities, options),
    coupon,
    listingCurrency,
    convert
  );

  const legacyInListingCurrency = convert(
    legacyAmountEur,
    "EUR",
    listingCurrency
  );

  if (!chargeAmountLooksConsistent(listingCurrencyTotal, legacyInListingCurrency)) {
    // The legacy amount arrives in EUR; charge it in the account's currency.
    const fallbackCurrency = getDefaultChargeCurrency();
    return {
      amount: roundMoney(
        fallbackCurrency === "EUR"
          ? legacyAmountEur
          : convert(legacyAmountEur, "EUR", fallbackCurrency)
      ),
      currency: fallbackCurrency,
      listingCurrency,
      listingCurrencyTotal,
      usedFallback: true,
    };
  }

  const chargeCurrency = resolveChargeCurrency(
    listingCurrency,
    chargeCurrenciesEnv
  );
  const amount = roundMoney(
    chargeCurrency === listingCurrency
      ? listingCurrencyTotal
      : convert(listingCurrencyTotal, listingCurrency, chargeCurrency)
  );

  return {
    amount,
    currency: chargeCurrency,
    listingCurrency,
    listingCurrencyTotal,
    usedFallback: false,
  };
}

/**
 * Currency a stored reservation was charged in. Reservations created before
 * the listing-currency charge have no value and were always charged in EUR.
 */
export function getReservationChargeCurrency(reservation: unknown): string {
  const raw = (reservation as { chargeCurrency?: unknown } | null | undefined)
    ?.chargeCurrency;
  const code = String(raw || "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : "EUR";
}

/**
 * Cross-rate from an ExchangeCurrency row (values are "currency per 1 USD"),
 * the same model the web and mobile converters use.
 */
export function convertWithRates(
  amount: number,
  fromCode: string,
  toCode: string,
  rates: Record<string, unknown> | null | undefined
): number {
  const value = Number(amount) || 0;
  const from = (fromCode || "USD").toUpperCase();
  const to = (toCode || "USD").toUpperCase();
  if (!rates || !value || from === to) return value;
  const fromRate = from === "USD" ? 1 : rates[from];
  const toRate = to === "USD" ? 1 : rates[to];
  if (
    typeof fromRate !== "number" ||
    typeof toRate !== "number" ||
    !Number.isFinite(fromRate) ||
    !Number.isFinite(toRate) ||
    fromRate <= 0
  ) {
    return value;
  }
  return roundMoney((value / fromRate) * toRate);
}
